import {InstructorDetails, DifficultyLevel} from '@udemy-v2/browse-course';
import {generateTrackingId} from '@udemy-v2/event-tracking';
import {getMonolithApiHeaders} from '@udemy-v2/api-fetch';
import {MultipleParallelFetchResult, useParallelFetch} from '@/lib/hooks/use-parallel-fetch';
import {TrackableDiscoveryItem} from '@udemy-v2/react-browse-event-tracking';
import {BrowseCourseWithInstructorDetails} from '@/types/course';
import {
    TaughtCoursesRequestConfig,
    TaughtCoursesApiResponse,
    ApiInstructor,
} from '@/types/taught-courses-api';
import {CourseFieldSelection, InstructorFieldSelection} from '@/types/taught-courses-api';
import {useUserHasOrganization} from '@/lib/hooks/use-user-has-organization';

const TAUGHT_PROFILE_COURSES_PATH = 'taught-profile-courses';
const TAUGHT_COURSES_PATH = 'taught-courses';

/**
 * Create a unique cache key from a request configuration
 */
export function generateCacheKey(config: TaughtCoursesRequestConfig): string {
    const key = {
        userId: config.userId,
        page: config.page ?? 1,
        pageSize: config.pageSize ?? 4,
        context: config.context ?? 'landing-page',
        subcontext: config.subcontext ?? 'morefrominstructor',
        organizationCoursesOnly: config.organizationCoursesOnly ?? false,
        filterHqCourses: config.filterHqCourses ?? true,
        ordering: config.ordering ? [...config.ordering].sort() : undefined,
        isInUserSubscription: config.isInUserSubscription ?? false,
        isGooglePartnerCourse: config.isGooglePartnerCourse ?? false,
    };
    return JSON.stringify(key);
}

/**
 * Format a taught courses request URL with query parameters
 */
export function generateQueryUrl(
    config: TaughtCoursesRequestConfig,
    hasOrganization: boolean = false,
): string {
    const params = new URLSearchParams();

    // Add basic parameters
    params.append('page', String(config.page || 1));
    params.append('page_size', String(config.pageSize || 4));

    // Add conditional parameters
    if (config.context) {
        params.append('context', config.context);
    }

    if (config.subcontext) {
        params.append('subcontext', config.subcontext);
    }

    params.append('organizationCoursesOnly', String(config.organizationCoursesOnly ?? false));
    params.append('filter_hq_courses', String(config.filterHqCourses ?? true));

    // Handle ordering
    if (config.ordering && config.ordering.length > 0) {
        params.append('ordering', config.ordering.join(','));
    } else {
        // Default ordering if none specified
        params.append('ordering', 'lang,-course_performance__revenue_30days');
    }

    // Add all course fields from the enum
    const courseFields = Object.values(CourseFieldSelection).join(',');
    params.append('fields[course]', courseFields);

    // Add is_in_user_subscription parameter only if user is a subscriber
    if (config.isInUserSubscription) {
        params.append('is_in_user_subscription', 'true');
    }

    // Add all instructor fields from the enum
    const instructorFields = Object.values(InstructorFieldSelection).join(',');
    params.append('fields[user]', instructorFields);

    // Org users get an empty response for taught-courses, so use taught-profile-courses path for org users.
    const taughtCoursesPath = hasOrganization ? TAUGHT_PROFILE_COURSES_PATH : TAUGHT_COURSES_PATH;

    return `users/${config.userId}/${taughtCoursesPath}/?${params.toString()}`;
}

/**
 * Map instructional level string to DifficultyLevel enum
 */
function adaptInstructionalLevel(level: string): DifficultyLevel {
    switch (level.toLowerCase()) {
        case 'beginner':
            return DifficultyLevel.BEGINNER;
        case 'intermediate':
            return DifficultyLevel.INTERMEDIATE;
        case 'expert':
            return DifficultyLevel.EXPERT;
        default:
            return DifficultyLevel.ALL_LEVELS;
    }
}

/**
 * Maps an API instructor to our internal InstructorDetails type
 */
function adaptInstructor(instructor: ApiInstructor): InstructorDetails | undefined {
    if (!instructor.url) {
        return undefined;
    }

    return {
        id: instructor.url?.split('/').filter(Boolean).pop() || '',
        name: instructor.display_name || instructor.name,
        title: instructor.title || instructor.job_title,
        description: instructor.description || '',
        images: {
            small: instructor.image_50x50 || instructor.image_100x100,
            medium: instructor.image_100x100 || instructor.image_75x75,
            large: instructor.image_200_H || instructor.image_125_H,
        },
        url: instructor.url,
        stats: {
            rating: instructor.avg_rating_recent || instructor.avg_rating || 0,
            reviewCount: instructor.total_num_reviews || 0,
            enrollmentCount: instructor.total_num_students || 0,
            courseCount:
                instructor.num_visible_taught_courses ?? instructor.num_published_courses ?? 0,
        },
    };
}

/**
 * Transform the API response into an array of CourseWithInstructorDetails objects
 */
export function adaptTaughtCoursesResponse(
    response: TaughtCoursesApiResponse,
): TrackableDiscoveryItem<BrowseCourseWithInstructorDetails>[] {
    return response.results
        .map((course) => {
            if (!course) return undefined;

            const transformedCourse: TrackableDiscoveryItem<BrowseCourseWithInstructorDetails> = {
                id: course.id.toString(),
                title: course.title,
                headline: course.headline || '',
                images: {
                    px48x27: course.image_48x27 || '',
                    px50x50: course.image_50x50 || '',
                    px75x75: course.image_75x75 || '',
                    px96x54: course.image_96x54 || '',
                    px100x100: course.image_100x100 || '',
                    px304x171: course.image_304x171 || '',
                },
                level: adaptInstructionalLevel(course.instructional_level_simple),
                rating: {
                    average: course.avg_rating || 0,
                    count: course.num_reviews || 0,
                },
                categories: course.context_info?.category
                    ? [
                          {
                              id: course.context_info.category.id.toString(),
                              name: course.context_info.category.title,
                              url: course.context_info.category.url,
                              subcategories: course.context_info.subcategory
                                  ? [
                                        {
                                            id: course.context_info.subcategory.id.toString(),
                                            name: course.context_info.subcategory.title,
                                            url: course.context_info.subcategory.url,
                                        },
                                    ]
                                  : [],
                          },
                      ]
                    : [],
                description: course.headline || '',
                frontendTrackingId: generateTrackingId(),
                trackingId: course.tracking_id ?? '',
                instructors:
                    course.visible_instructors
                        ?.map(adaptInstructor)
                        .filter((i): i is InstructorDetails => i !== undefined) || [],
                learningOutcomes: [],
                prerequisites: [],
                targetAudience: [],
                enrollable: true,
                isFree: false,
                isPracticeTestCourse: course.is_practice_test_course || false,
                locale: course.locale?.locale || 'en_US',
                urlAutoEnroll: course.url || '',
                urlCourseLanding: course.url || '',
                urlMobileNativeDeeplink: course.url || '',
                isInUserSubscription: course.is_in_user_subscription ?? false,
                availabilityStatus: course.subscription_availability_status,
            };

            // Transform Google partner collection inclusion
            if (!!course.is_google_partner_course) {
                transformedCourse.contentCollectionInfo = {
                    isInGooglePartnerCollection: true,
                };
            }

            return transformedCourse;
        })
        .filter((course): course is BrowseCourseWithInstructorDetails => course !== undefined);
}

/**
 * Result of fetching taught courses for multiple instructors
 */
export interface InstructorTaughtCourses
    extends Omit<
        MultipleParallelFetchResult<
            TrackableDiscoveryItem<BrowseCourseWithInstructorDetails>[],
            string
        >,
        'data'
    > {
    /**
     * Map of instructor URL to arrays of BrowseCourse objects
     * Access courses for a specific instructor using: courseMap[instructorUrl]
     */
    coursesByInstructorsByUrl: Record<
        string,
        TrackableDiscoveryItem<BrowseCourseWithInstructorDetails>[]
    >;
    /**
     * Map of instructor URL to InstructorDetails
     */
    instructorDetailsByUrl: Record<string, InstructorDetails>;
}

/**
 * Hook to fetch and cache taught courses and instructor details for multiple instructors
 * @param configs - Array of configurations for taught courses requests
 * @returns Object containing mapping of instructor URL to courses data, loading state, and error state
 *
 * @example
 * // Fetch courses taught by multiple instructors
 * const { coursesByInstructorsByUrl, isLoading, error } = useMultipleInstructorTaughtCourses([
 *   { userId: '1234', pageSize: 8 },
 *   { userId: '5678', pageSize: 4 }
 * ]);
 *
 * // Access courses for a specific instructor
 * const instructorCourses = coursesByInstructorsByUrl['/user/1234/'] || [];
 */
export const useFetchCoursesByInstructors = (
    configs: TaughtCoursesRequestConfig[],
): InstructorTaughtCourses => {
    const hasOrganization = useUserHasOrganization();
    const fetchInstructorCourses = async (config: TaughtCoursesRequestConfig) => {
        const url = generateQueryUrl(config, hasOrganization);
        const response = await fetch(`/api-2.0/${url}`, {
            method: 'GET',
            headers: {
                ...getMonolithApiHeaders(),
            },
        });

        if (!response.ok) {
            throw new Error(
                `Failed to fetch taught courses: ${response.status} ${response.statusText}`,
            );
        }

        const data: TaughtCoursesApiResponse = await response.json();
        const transformedData = adaptTaughtCoursesResponse(data);

        return {
            key: config.userId,
            data: transformedData,
        };
    };

    const result = useParallelFetch<
        TrackableDiscoveryItem<BrowseCourseWithInstructorDetails>[],
        TaughtCoursesRequestConfig,
        string
    >(configs, fetchInstructorCourses);

    // Build course and instructor maps using URLs as keys
    const {coursesByInstructorsByUrl, instructorDetailsByUrl} = Object.entries(result.data).reduce<{
        coursesByInstructorsByUrl: Record<string, BrowseCourseWithInstructorDetails[]>;
        instructorDetailsByUrl: Record<string, InstructorDetails>;
    }>(
        (acc, [userId, courses]) => {
            // Find the matching config to get the instructorUrl
            const config = configs.find((c) => c.userId === userId);
            if (!config?.instructorUrl) return acc;

            // Use the instructorUrl from the config as the key
            acc.coursesByInstructorsByUrl[config.instructorUrl] = courses;

            // Get all unique instructors from all courses
            courses.forEach((course) => {
                course.instructors.forEach((instructor) => {
                    // Only add if this instructor URL isn't already in the map
                    if (instructor.url && !acc.instructorDetailsByUrl[instructor.url]) {
                        acc.instructorDetailsByUrl[instructor.url] = instructor;
                    }
                });
            });

            return acc;
        },
        {coursesByInstructorsByUrl: {}, instructorDetailsByUrl: {}},
    );

    return {
        isLoading: result.isLoading,
        error: result.error,
        errorMap: result.errorMap,
        coursesByInstructorsByUrl,
        instructorDetailsByUrl,
    };
};
